/*---------------------------------------------------------------------------------------------
 * Drop Zone Provider
 * Provides drop zone functionality for drag and drop
 *--------------------------------------------------------------------------------------------*/

import React, { useRef, useCallback, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { cn } from '@/lib/utils';
import {
  visibleComponentsByZoneSelector,
  componentPositionsState,
  DropZone,
  ComponentType
} from '@/state/dragDrop';
import { DynamicDropOverlay } from './DynamicDropOverlay';

interface DropZoneProviderProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * DropZoneProvider manages global drag state and handles component position updates
 * when components are dropped. Supports two drop behaviors:
 * - Swap: exchange positions between the dragged component and a target component
 * - Edge placement (2-component): place dragged component at an edge, move other to opposite
 */
export const DropZoneProvider: React.FC<DropZoneProviderProps> = ({
  children,
  className
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [draggedComponent, setDraggedComponent] = useState<ComponentType | undefined>();
  const [isDropInProgress, setIsDropInProgress] = useState(false);
  
  const visibleComponentsByZone = useRecoilValue(visibleComponentsByZoneSelector);
  const setComponentPositions = useSetRecoilState(componentPositionsState);

  /**
   * Handles component drops and updates positions based on drop behavior
   * Swap: switchWith provided — exchange two components' zones
   * Side-flip (3-component): no switchWith — mirror all components to opposite side
   * Edge placement (2-component): no switchWith — place dragged at edge, other at opposite
   */
  const handleDrop = useCallback((targetZone: DropZone, event: DragEvent, switchWith?: ComponentType) => {
    if (!draggedComponent) return;

    setIsDropInProgress(true);

    try {
      const dragData = JSON.parse(event.dataTransfer?.getData('application/vnd.crimson-raven.component') || '{}');
      if (dragData.componentType !== draggedComponent) return;

      setComponentPositions(currentPositions => {
        const newPositions = { ...currentPositions };

        const draggedZone = Object.entries(visibleComponentsByZone)
          .find(([_, comps]) => comps.includes(draggedComponent))?.[0] as DropZone | undefined;
        if (!draggedZone) return newPositions;

        if (switchWith) {
          const targetZoneResolved = Object.entries(visibleComponentsByZone)
            .find(([_, comps]) => comps.includes(switchWith))?.[0] as DropZone | undefined;
          if (!targetZoneResolved) return newPositions;

          newPositions[draggedComponent as keyof typeof newPositions] = { zone: targetZoneResolved, order: 0 };
          newPositions[switchWith as keyof typeof newPositions] = { zone: draggedZone, order: 0 };
          return newPositions;
        }

        const mirror: Record<DropZone, DropZone> = {
          'left': 'right',
          'right': 'left',
          'top-left': 'top-right',
          'bottom-left': 'bottom-right',
          'top-right': 'top-left',
          'bottom-right': 'bottom-left'
        };

        const allVisible = Object.values(visibleComponentsByZone).flat();

        if (allVisible.length === 3) {
          for (const comp of allVisible) {
            const currentZone = Object.entries(visibleComponentsByZone)
              .find(([_, comps]) => comps.includes(comp))?.[0] as DropZone;
            if (currentZone) {
              newPositions[comp as keyof typeof newPositions] = { zone: mirror[currentZone], order: 0 };
            }
          }
          return newPositions;
        }

        const other = allVisible.find(c => c !== draggedComponent);
        if (other) {
          const opposite: Record<DropZone, DropZone> = {
            'left': 'right',
            'right': 'left',
            'top-left': 'bottom-left',
            'bottom-left': 'top-left',
            'top-right': 'bottom-right',
            'bottom-right': 'top-right'
          };
          newPositions[draggedComponent as keyof typeof newPositions] = { zone: targetZone, order: 0 };
          newPositions[other as keyof typeof newPositions] = { zone: opposite[targetZone] ?? 'right', order: 0 };
        }

        return newPositions;
      });

      setIsDragActive(false);
      setDraggedComponent(undefined);
      setIsDropInProgress(false);
    } catch (error) {
      console.error('Error handling drop:', error);
      setIsDragActive(false);
      setDraggedComponent(undefined);
      setIsDropInProgress(false);
    }
  }, [draggedComponent, visibleComponentsByZone, setComponentPositions]);

  /**
   * Handles drag start events and sets the dragged component
   * @param event - The drag start event
   */
  const handleDragStart = useCallback((event: DragEvent) => {
    try {
      const dragData = JSON.parse(event.dataTransfer?.getData('application/vnd.crimson-raven.component') || '{}');
      setDraggedComponent(dragData.componentType);
      setIsDragActive(true);
    } catch (error) {
      console.error('Error parsing drag data:', error);
    }
  }, []);

  /**
   * Handles drag end events with delayed cleanup to ensure drop events process first
   */
  const handleDragEnd = useCallback(() => {
    setTimeout(() => {
      if (!isDropInProgress) {
        setIsDragActive(false);
        setDraggedComponent(undefined);
      }
    }, 500);
  }, [isDropInProgress]);

  // Global drag event listeners
  React.useEffect(() => {
    const handleDragStartGlobal = (event: DragEvent) => {
      if (event.dataTransfer?.types.includes('application/vnd.crimson-raven.component')) {
        handleDragStart(event);
      }
    };

    const handleDragEndGlobal = () => {
      handleDragEnd();
    };

    const handleDragCancel = () => {
      if (!isDropInProgress) {
        setIsDragActive(false);
        setDraggedComponent(undefined);
      }
    };

    document.addEventListener('dragstart', handleDragStartGlobal);
    document.addEventListener('dragend', handleDragEndGlobal);
    document.addEventListener('dragcancel', handleDragCancel);

    return () => {
      document.removeEventListener('dragstart', handleDragStartGlobal);
      document.removeEventListener('dragend', handleDragEndGlobal);
      document.removeEventListener('dragcancel', handleDragCancel);
      setIsDragActive(false);
      setDraggedComponent(undefined);
    };
  }, [handleDragStart, handleDragEnd]);

  return (
    <div
      ref={containerRef}
      className={cn("h-full w-full relative", className)}
      onDragOver={(e) => e.preventDefault()}
    >
      {children}
      
      <DynamicDropOverlay
        isActive={isDragActive}
        draggedComponent={draggedComponent}
        containerRef={containerRef}
        onDrop={handleDrop}
      />
    </div>
  );
};




